import { supabase } from '@/integrations/supabase/client';

export type AITraceEventType =
  | 'session_start'
  | 'user_message'
  | 'ai_response'
  | 'branch_card_shown'
  | 'branch_choice'
  | 'recommendation_shown'
  | 'recommendation_click'
  | 'stage_click'
  | 'navigation_click'
  | 'login_redirect'
  | 'signup_redirect'
  | 'conversation_resumed'
  | 'conversation_migrated'
  | 'booking_started'
  | 'booking_completed'
  | 'payment_success'
  | 'crisis_detected'
  | 'drop_off';

export type AITraceBranchChoice = 'continue' | 'services' | 'save_later';
export type AITraceLengthBucket = 'short' | 'medium' | 'long';
export type AITraceContext = 'homepage' | 'dashboard';
export type AITraceResponseType = 'clarification' | 'summary' | 'recommendation' | 'other';

export interface AITraceEvent {
  event_type: AITraceEventType;
  event_label?: string | null;
  turn_index?: number | null;
  input_text?: string | null;
  ai_response?: string | null; // legacy, no longer recorded as full text
  input_word_count?: number | null;
  input_char_count?: number | null;
  input_length_bucket?: AITraceLengthBucket | null;
  used_chip?: boolean;
  used_structured_input?: boolean;
  added_details?: boolean;
  branch_choice?: AITraceBranchChoice | null;
  problem_type?: string | null;
  stage?: number | null;
  recommended_service?: boolean;
  clicked_recommendation?: boolean;
  target_path?: string | null;
  conversation_id?: string | null;
  guest_session_id?: string | null;
  // New structured fields
  context?: AITraceContext | null;
  response_length?: AITraceLengthBucket | null;
  contains_question?: boolean;
  contains_recommendation?: boolean;
  response_type?: AITraceResponseType | null;
  exit_turn?: number | null;
  exit_stage?: number | null;
  metadata?: Record<string, unknown>;
}

export function classifyInputLength(text: string): {
  words: number;
  chars: number;
  bucket: AITraceLengthBucket;
} {
  const trimmed = (text || '').trim();
  const chars = trimmed.length;
  const words = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
  let bucket: AITraceLengthBucket = 'medium';
  if (words <= 5) bucket = 'short';
  else if (words >= 20) bucket = 'long';
  return { words, chars, bucket };
}

const PROBLEM_PATTERNS: { type: string; pattern: RegExp }[] = [
  { type: 'anxiety', pattern: /anxi|焦虑|panic|nervous|worry/i },
  { type: 'relationship', pattern: /relationship|partner|marriage|breakup|关系|伴侣|婚姻|分手|family|家庭/i },
  { type: 'sleep', pattern: /sleep|insomnia|睡眠|失眠|tired|疲惫|exhaust/i },
  { type: 'burnout', pattern: /burnout|overwhelmed|exhausted|倦怠|过劳|压力|stress/i },
  { type: 'lost', pattern: /lost|purpose|meaningless|迷茫|找不到方向|empty|空虚/i },
  { type: 'depression', pattern: /depress|sad|抑郁|low mood|hopeless/i },
  { type: 'grief', pattern: /grief|loss|bereave|mourning|悲痛|失去/i },
  { type: 'trauma', pattern: /trauma|ptsd|abuse|创伤|virgin/i },
];

export function classifyProblemType(text: string): string | null {
  if (!text) return null;
  for (const { type, pattern } of PROBLEM_PATTERNS) {
    if (pattern.test(text)) return type;
  }
  return null;
}

const RECOMMENDATION_PATTERNS = /recommend|suggest|practitioner|therapist|book(ing)?|appointment|疗愈师|咨询师|推荐|预约|建议你|你可以试试/i;
const QUESTION_PATTERNS = /\?|？|could you|can you tell|how (do|are|long)|when did|what (about|is|are)|why |你能|你愿意|可以告诉|多久|怎么样|什么时候|为什么/i;

/**
 * Classify the AI assistant reply into a structured shape — without storing
 * the full text. We only persist length bucket, type, and boolean flags.
 */
export function classifyAIResponse(text: string): {
  length: AITraceLengthBucket;
  containsQuestion: boolean;
  containsRecommendation: boolean;
  type: AITraceResponseType;
} {
  const trimmed = (text || '').trim();
  const words = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
  let length: AITraceLengthBucket = 'medium';
  if (words <= 30) length = 'short';
  else if (words >= 120) length = 'long';

  const containsQuestion = QUESTION_PATTERNS.test(trimmed);
  const containsRecommendation = RECOMMENDATION_PATTERNS.test(trimmed);

  let type: AITraceResponseType = 'other';
  if (containsRecommendation) type = 'recommendation';
  else if (containsQuestion) type = 'clarification';
  else if (length === 'long') type = 'summary';

  return { length, containsQuestion, containsRecommendation, type };
}

let cachedDeviceType: 'mobile' | 'desktop' | null = null;
function getDeviceType(): 'mobile' | 'desktop' {
  if (cachedDeviceType) return cachedDeviceType;
  if (typeof window === 'undefined') {
    cachedDeviceType = 'desktop';
    return cachedDeviceType;
  }
  const ua = window.navigator.userAgent || '';
  const isMobile = /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(ua) || window.innerWidth < 768;
  cachedDeviceType = isMobile ? 'mobile' : 'desktop';
  return cachedDeviceType;
}

/**
 * Fire-and-forget trace event. Never throws — never blocks UI.
 */
export async function trackAIEvent(event: AITraceEvent): Promise<void> {
  try {
    // Strip ai_response full text — we only store structured fields now.
    const { ai_response: _stripped, ...safe } = event;
    const payload = {
      ...safe,
      device_type: getDeviceType(),
    };
    void supabase.functions
      .invoke('ai-trace', { body: { events: [payload] } })
      .catch((err) => {
        if (typeof console !== 'undefined') {
          console.debug('[ai-trace] suppressed error', err);
        }
      });
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.debug('[ai-trace] suppressed error', err);
    }
  }
}

/**
 * Send a trace event using sendBeacon when the page is closing.
 * Falls back to trackAIEvent if beacon unavailable.
 */
export function trackAIEventBeacon(event: AITraceEvent, supabaseUrl: string, anonKey: string): void {
  try {
    const { ai_response: _stripped, ...safe } = event;
    const payload = JSON.stringify({
      events: [{ ...safe, device_type: getDeviceType() }],
    });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const url = `${supabaseUrl}/functions/v1/ai-trace`;
      // Beacon API doesn't allow custom headers, so we append apikey via URL? Edge function accepts no-auth.
      // Just send blob; ai-trace edge function tolerates missing auth.
      navigator.sendBeacon(url, blob);
      return;
    }
    void trackAIEvent(event);
  } catch {
    /* swallow */
  }
}
